import { createInertiaApp, router } from "@inertiajs/react";
import { createElement, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { ShowFlashProvider } from "@sprout/flash.tsx";
import Layout from "@components/layout";
import { InertiaErrorBoundary } from "@/errors/inertia_error_boundary.tsx";
import "@/utils/axios_config";
import i18n from "@entrypoints/i18n.ts";

export type ForethoughtProps = {
  enabled?: boolean;
  apiKey?: string;
  email?: string;
  fullName?: string;
  loggedIn?: boolean;
};

function ensureForethought(pageProps: Record<string, unknown>) {
  try {
    const ft = (pageProps as { forethought?: ForethoughtProps })?.forethought;
    if (!ft?.enabled || !ft.loggedIn) return;

    if (!document.getElementById("forethought-widget-embed-script")) {
      const s = document.createElement("script");
      s.src = "https://solve-widget.forethought.ai/embed.js";
      s.id = "forethought-widget-embed-script";
      s.setAttribute("data-api-key", String(ft.apiKey || ""));
      s.setAttribute("data-ft-Email", String(ft.email || ""));
      s.setAttribute("data-ft-Full-Name", String(ft.fullName || ""));
      s.setAttribute("data-ft-Logged-In", "true");
      s.setAttribute("data-ft-workflow-tag", "my-gh");
      s.setAttribute("config-ft-greeting-message", i18n.t("forethought_greeting", { ns: "misc" }));
      s.defer = true;
      document.body.appendChild(s);
    }
  } catch (e) {
    console.error("Failed to initialize Forethought widget:", e);
  }
}

router.on("success", (event) => {
  ensureForethought(event.detail.page.props);
});

// Temporary type definition, until @inertiajs/react provides one
type ResolvedComponent = {
  default: ReactNode;
  layout?: (page: ReactNode) => ReactNode;
};

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.netlify.app/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar for now because it doesn't work with CSP unsafe-inline
  // https://github.com/inertiajs/progress/issues/21
  progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: true,
    });
    const page = pages[`../pages/${name}.tsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    // @ts-expect-error Inertia pages have a layout property
    page.default.layout =
      // @ts-expect-error Inertia pages have a layout property
      page.default.layout || ((content: ReactNode) => <Layout>{content}</Layout>);
    return page;
  },

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        createElement(
          ShowFlashProvider,
          null,
          createElement(InertiaErrorBoundary, null, createElement(App, props)),
        ),
      );
      // ensure widget exists on initial load too
      // @ts-expect-error router.page is not typed here
      ensureForethought(router?.page?.props || {});
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
      );
    }
  },
});
